import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, ChevronDown, Download, FileSpreadsheet, Filter, MoreHorizontal, MoreVertical, Pencil, Archive, Plus, Search, Upload, X, Copy, Trash2, RotateCcw, History, Maximize2, FileDown } from 'lucide-react';
import './AgendamentosOdontologiaPipeline.css';
import * as XLSX from 'xlsx';

const ETAPAS = [
  { id: 'leads_novos', nome: 'Leads Novos' },
  { id: 'novos_associados', nome: 'Novos Associados' },
  { id: 'tratamentos_parados', nome: 'Tratamentos Parados' },
  { id: 'nao_movimentado', nome: 'Não Movimentado' },
  { id: 'movimentado', nome: 'Movimentado' },
  { id: 'ortodontia', nome: 'Ortodontia' },
  { id: 'tratamentos_finalizados', nome: 'Tratamentos Finalizados' },
  { id: 'carteira_lucas', nome: 'carteira do dr. Lucas' }
];

// Etapas criadas pelo usuário (vindas do servidor), além das fixas acima.
const ETAPAS_DINAMICAS = new Set();
const ETAPAS_CORES = new Map();
const CORES_CARTEIRA = [
  '#4D56D8', '#2563EB', '#0EA5E9', '#0891B2', '#14B8A6', '#10B981', '#22C55E', '#84CC16',
  '#EAB308', '#F59E0B', '#F97316', '#EF4444', '#DC2626', '#E11D48', '#EC4899', '#D946EF',
  '#A855F7', '#7C3AED', '#64748B', '#334155',
];

const STATUS = [
  ['sem_status', 'Sem status'],
  ['mensagem_enviada', 'Mensagem enviada'],
  ['agendado', 'Agendado'],
  ['sem_retorno', 'Sem retorno'],
  ['retornou', 'Retornou'],
  ['interessado', 'Interessado'],
  ['nao_tem_interesse', 'Não tem interesse'],
  ['aguardando_contato', 'Aguardando contato'],
  ['compareceu', 'Compareceu']
];

const texto = (v) => String(v == null ? '' : v);
const slug = (v) => texto(v).toLocaleLowerCase('pt-BR').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
const dataBR = (v) => {
  const m = texto(v).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : texto(v);
};

async function api(path, options = {}) {
  const resposta = await fetch(`/api${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
      'Content-Type': 'application/json',
      ...(options.headers || {})
    }
  });
  const body = await resposta.text();
  let dados = {};
  try { dados = body ? JSON.parse(body) : {}; } catch (_) { dados = { error: body }; }
  if (!resposta.ok) throw new Error(dados.error || dados.message || `Erro HTTP ${resposta.status}`);
  return dados;
}

function lista(dados) {
  if (Array.isArray(dados)) return dados;
  return dados.items || dados.rows || dados.agendas || dados.data || [];
}

function normalizarAgenda(agenda, setor) {
  return {
    ...agenda,
    id: agenda.id,
    setor,
    nome: agenda.nome || agenda.especialidade || 'Agenda odontológica',
    data: texto(agenda.data).slice(0, 10),
    horarios: Array.isArray(agenda.horarios) ? agenda.horarios : []
  };
}

function normalizarPaciente(linha, agenda, indice) {
  const status = slug(linha.status || linha.situacao || 'aguardando_contato');
  const extras = linha.dados_extras && typeof linha.dados_extras === 'object' ? linha.dados_extras : {};
  const etapaInformada = slug(linha.etapa || extras.etapa || linha.fase || linha.coluna || linha.categoria || '');
  const etapa = (ETAPAS.some((item) => item.id === etapaInformada) || ETAPAS_DINAMICAS.has(etapaInformada))
    ? etapaInformada
    : (status === 'compareceu' ? 'movimentado' : 'novos_associados');
  return {
    ...linha,
    id: linha.id || `${agenda.id}-${indice}`,
    agendaId: agenda.id,
    nome: linha.paciente || linha.nome || 'Paciente sem nome',
    contato: linha.contato || linha.telefone || '',
    status,
    etapa,
    data: linha.data || agenda.data || '',
    observacao: linha.observacao || linha.observacoes || '',
    agendaNome: agenda.especialidade || agenda.nome || 'Odontologia',
    horario: linha.horario || ''
  };
}

const extraPaciente = (item, campo) => texto(((item && item.dados_extras) || {})[campo]);

const COLS_PIPELINE = [
  ['nome', 'Nome ↕', 240],
  ['contato', 'Telefone', 160],
  ['tratamentos', 'Tratamentos', 200],
  ['valor_total', 'Valor/total', 130],
  ['dentista', 'Dentista', 170],
  ['virou_ficha', 'Virou Ficha', 120],
  ['status', 'Status', 170],
  ['data', 'Data do envio', 130],
  ['obs', 'Observações', 260],
  ['acoes', 'Ações', 118],
];
const LARG_PADRAO = Object.fromEntries(COLS_PIPELINE.map(([chave, , largura]) => [chave, largura]));

function CampoEditavel({ valor, onSave, placeholder }) {
  const [textoLocal, setTextoLocal] = useState(texto(valor));
  useEffect(() => setTextoLocal(texto(valor)), [valor]);
  return <input value={textoLocal} placeholder={placeholder} onChange={(e) => setTextoLocal(e.target.value)} onBlur={() => { if (textoLocal !== texto(valor)) onSave(textoLocal); }} />;
}

// ═══ Importação de pacientes por planilha ═══
const ALIAS_IMPORTACAO = {
  nome: ['nome', 'paciente', 'cliente', 'associado', 'beneficiario'],
  contato: ['telefone', 'contato', 'celular', 'whatsapp', 'fone'],
  status: ['status', 'situacao'],
  data: ['data'],
  observacao: ['observ', 'obs', 'anotac'],
  horario: ['horario', 'hora'],
  tratamentos: ['tratamento', 'procedimento'],
  valor_total: ['valor', 'total', 'preco'],
  dentista: ['dentista', 'profissional', 'doutor'],
  virou_ficha: ['ficha'],
};
const ROTULO_CAMPO = { nome: 'Nome', contato: 'Telefone', status: 'Status', data: 'Data', observacao: 'Observação', horario: 'Horário', tratamentos: 'Tratamentos', valor_total: 'Valor/total', dentista: 'Dentista', virou_ficha: 'Virou Ficha' };
const doisDig = (n) => String(n).padStart(2, '0');

const paraISOImportacao = (v) => {
  const montar = (y, m, d) => {
    if (y < 1900 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) return '';
    return `${y}-${doisDig(m)}-${doisDig(d)}`;
  };
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    const ajustada = new Date(v.getTime() + 12 * 3600000);
    return montar(ajustada.getFullYear(), ajustada.getMonth() + 1, ajustada.getDate());
  }
  if (typeof v === 'number' && v > 20000 && v < 80000) {
    const d = new Date(Math.round((v - 25569) * 86400000));
    return montar(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
  }
  const s = texto(v).trim();
  let m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/.exec(s);
  if (m) {
    let ano = Number(m[3]);
    if (ano < 100) ano += 2000;
    return montar(ano, Number(m[2]), Number(m[1]));
  }
  m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (m) return montar(Number(m[1]), Number(m[2]), Number(m[3]));
  return '';
};

const horaImportacao = (v) => {
  if (v instanceof Date && !Number.isNaN(v.getTime())) return `${doisDig(v.getHours())}:${doisDig(v.getMinutes())}`;
  if (typeof v === 'number' && v >= 0 && v < 1) {
    const minutos = Math.round(v * 24 * 60);
    return `${doisDig(Math.floor(minutos / 60))}:${doisDig(minutos % 60)}`;
  }
  const m = /^(\d{1,2}):(\d{2})/.exec(texto(v).trim());
  return m ? `${doisDig(m[1])}:${m[2]}` : '';
};

const statusImportacao = (v) => {
  const s = slug(v);
  if (!s) return 'sem_status';
  const achado = STATUS.find(([id, nome]) => id === s || slug(nome) === s);
  return achado ? achado[0] : 'sem_status';
};

const valorImportacao = (v) => {
  if (typeof v === 'number' && Number.isFinite(v)) return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  return texto(v).trim();
};

const fichaImportacao = (v) => {
  const s = slug(v);
  if (!s) return '';
  if (['sim', 's', 'yes', 'x', '1', 'ok', 'virou'].includes(s)) return 'sim';
  if (['nao', 'n', 'no', '0'].includes(s)) return 'nao';
  return '';
};

const extrasImportacao = (l) => Object.fromEntries(
  ['tratamentos', 'valor_total', 'dentista', 'virou_ficha'].map((k) => [k, l[k]]).filter(([, v]) => v)
);

const chavePaciente = (nome, contato) => `${slug(nome)}|${texto(contato).replace(/\D/g, '')}`;

const lerPlanilhaPacientes = async (arquivo) => {
  const livro = XLSX.read(await arquivo.arrayBuffer(), { type: 'array', cellDates: true });
  const aba = livro.SheetNames[0];
  const matriz = XLSX.utils.sheet_to_json(livro.Sheets[aba], { header: 1, defval: '' });

  let linhaCabecalho = -1;
  let mapa = {};
  for (let i = 0; i < Math.min(matriz.length, 20); i += 1) {
    const cabecalho = (matriz[i] || []).map((c) => slug(c));
    const encontrado = {};
    Object.entries(ALIAS_IMPORTACAO).forEach(([campo, termos]) => {
      const indice = cabecalho.findIndex((c, idx) => c && termos.some((t) => c.includes(t)) && !Object.values(encontrado).includes(idx));
      if (indice >= 0) encontrado[campo] = indice;
    });
    if (encontrado.nome !== undefined) { linhaCabecalho = i; mapa = encontrado; break; }
  }
  if (linhaCabecalho < 0) mapa = { nome: 0, contato: 1 };

  const titulos = linhaCabecalho >= 0 ? (matriz[linhaCabecalho] || []) : [];
  const colunas = Object.entries(mapa).map(([campo, indice]) => `${ROTULO_CAMPO[campo]} ← "${texto(titulos[indice]).trim() || `coluna ${indice + 1}`}"`);

  const pegar = (linha, campo) => (mapa[campo] === undefined ? '' : linha[mapa[campo]]);
  let semNome = 0;
  let datasInvalidas = 0;
  const validas = [];
  matriz.slice(linhaCabecalho + 1).forEach((linha) => {
    const nome = texto(pegar(linha, 'nome')).trim();
    if (!nome) { if ((linha || []).some((c) => texto(c).trim())) semNome += 1; return; }
    const dataBruta = pegar(linha, 'data');
    const data = paraISOImportacao(dataBruta);
    if (texto(dataBruta).trim() && !data) datasInvalidas += 1;
    validas.push({
      nome,
      contato: texto(pegar(linha, 'contato')).trim(),
      status: statusImportacao(pegar(linha, 'status')),
      data,
      observacao: texto(pegar(linha, 'observacao')).trim(),
      horario: horaImportacao(pegar(linha, 'horario')),
      tratamentos: texto(pegar(linha, 'tratamentos')).trim(),
      valor_total: valorImportacao(pegar(linha, 'valor_total')),
      dentista: texto(pegar(linha, 'dentista')).trim(),
      virou_ficha: fichaImportacao(pegar(linha, 'virou_ficha')),
    });
  });

  return { aba, colunas, validas, semNome, datasInvalidas };
};

// Campo de data sempre em dd/mm/aaaa (o input type="date" segue o idioma do sistema).
function DataBuscaBR({ value, onChange }) {
  const paraBR = (iso) => (/^\d{4}-\d{2}-\d{2}$/.test(iso || '') ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '');
  const [textoData, setTextoData] = useState(paraBR(value));
  const nativo = useRef(null);

  useEffect(() => { setTextoData(paraBR(value)); }, [value]);

  const digitar = (bruto) => {
    const d = bruto.replace(/\D/g, '').slice(0, 8);
    const formatado = d.length > 4 ? `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}` : d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
    setTextoData(formatado);
    if (d.length === 8) {
      const iso = `${d.slice(4)}-${d.slice(2, 4)}-${d.slice(0, 2)}`;
      const data = new Date(`${iso}T12:00:00`);
      if (!Number.isNaN(data.getTime()) && data.getDate() === Number(d.slice(0, 2))) onChange(iso);
    } else if (!d.length) {
      onChange('');
    }
  };

  const abrirCalendario = () => {
    try { nativo.current?.showPicker(); } catch (_) { nativo.current?.click(); }
  };

  return (
    <span className="agset-data-br">
      <input type="text" inputMode="numeric" placeholder="dd/mm/aaaa" value={textoData} onChange={(e) => digitar(e.target.value)} aria-label="Pesquisar por data" />
      <button type="button" title="Abrir calendário" onClick={abrirCalendario}><CalendarDays size={14} /></button>
      {textoData && <button type="button" title="Limpar data" onClick={() => { setTextoData(''); onChange(''); }}><X size={13} /></button>}
      <input ref={nativo} type="date" tabIndex={-1} aria-hidden="true" className="agset-data-br-nativo" value={value || ''} onChange={(e) => onChange(e.target.value)} />
    </span>
  );
}

const LIMITE_PAGINA = 30;
function consultaRegistros({ etapa = '', pagina = 1, status = '', busca = '', data = '', ordem = 'nome', direcao = 'asc' }) {
  const p = new URLSearchParams({ page: String(pagina), limit: String(LIMITE_PAGINA), ordem, direcao });
  if (etapa) p.set('etapa', etapa);
  if (status) p.set('status', status);
  if (busca) p.set('busca', busca);
  if (data) p.set('data', data);
  return p.toString();
}
function linhaDoServidor(linha) {
  const base = normalizarPaciente(linha, { id: linha.agendaId, data: texto(linha.dataAgenda).slice(0, 10), especialidade: linha.especialidade, nome: linha.agendaNome }, 0);
  return linha.etapa ? { ...base, etapa: linha.etapa } : base;
}
async function buscarRegistros(filtros) {
  const r = await api(`/odontologia-pipeline/registros?${consultaRegistros(filtros)}`);
  return { rows: (r.rows || []).map(linhaDoServidor), counts: r.counts || {}, total: Number(r.total || 0), totalPages: Number(r.totalPages || 1) };
}
async function buscarTodosRegistros(filtros) {
  const primeira = await buscarRegistros({ ...filtros, pagina: 1 });
  const todas = [...primeira.rows];
  for (let inicio = 2; inicio <= primeira.totalPages; inicio += 8) {
    const lote = [];
    for (let k = inicio; k < inicio + 8 && k <= primeira.totalPages; k += 1) lote.push(buscarRegistros({ ...filtros, pagina: k }));
    (await Promise.all(lote)).forEach((r) => todas.push(...r.rows));
  }
  return todas;
}

export default function AgendamentosSetorial({ setor = 'odontologia' }) {
  const [agendas, setAgendas] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [contagensEtapa, setContagensEtapa] = useState({});
  const [recarregarTick, setRecarregarTick] = useState(0);
  const [buscaServidor, setBuscaServidor] = useState('');
  const [carregandoPagina, setCarregandoPagina] = useState(false);
  const primeiraCargaRef = useRef(false);
  const requisicaoRef = useRef(0);
  const [etapaAtiva, setEtapaAtiva] = useState('novos_associados');
  const [busca, setBusca] = useState('');
  const [dataBusca, setDataBusca] = useState('');
  const [ampliado, setAmpliado] = useState(false);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('souli:ampliar-tabela', { detail: { ampliado } }));
  }, [ampliado]);

  useEffect(() => () => {
    window.dispatchEvent(new CustomEvent('souli:ampliar-tabela', { detail: { ampliado: false } }));
  }, []);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [ordenacao, setOrdenacao] = useState('nome');
  const [ordemInversa, setOrdemInversa] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [selecionados, setSelecionados] = useState([]);
  const [copiado, setCopiado] = useState(null);
  const [modalNovo, setModalNovo] = useState(false);
  const [novo, setNovo] = useState({ nome: '', contato: '', status: 'aguardando_contato', etapa: 'novos_associados', data: '', horario: '', observacao: '' });

  const [whatsappTelefoneAtivo, setWhatsappTelefoneAtivo] = useState(
    () => localStorage.getItem('agenda-odontologia-whatsapp-telefone') === '1'
  );

  const alternarWhatsappTelefone = (valor) => {
    setWhatsappTelefoneAtivo(valor);
    localStorage.setItem(
      'agenda-odontologia-whatsapp-telefone',
      valor ? '1' : '0'
    );
  };

  const abrirWhatsappTelefone = (telefone) => {
    const numero = String(telefone || '').replace(/\D/g, '');

    if (!numero) return;

    const numeroComPais =
      numero.length === 10 || numero.length === 11
        ? `55${numero}`
        : numero;

    window.open(
      `https://wa.me/${numeroComPais}`,
      '_blank',
      'noopener,noreferrer'
    );
  };


  const [lixeira, setLixeira] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [etapasEstado, setEtapasEstado] = useState({});
  const [etapasNomes, setEtapasNomes] = useState({});
  const [etapaEditando, setEtapaEditando] = useState(null);
  const [nomeEtapa, setNomeEtapa] = useState('');
  const [historicoModal, setHistoricoModal] = useState(null);
  const [larg, setLarg] = useState(() => {
    try {
      return {
        ...LARG_PADRAO,
        ...(JSON.parse(localStorage.getItem('odonto-larg')) || {}),
        acoes: 118
      };
    } catch (e) {
      return { ...LARG_PADRAO, acoes: 118 };
    }
  });
  const largRef = useRef(larg);
  const salvarLarg = () => { try { localStorage.setItem('odonto-larg', JSON.stringify(largRef.current)); } catch (e) { /* sem armazenamento */ } };
  const iniciarResize = (e, chave) => {
    e.preventDefault();
    e.stopPropagation();
    const x0 = e.clientX;
    const w0 = largRef.current[chave];
    document.body.classList.add('agset-redimensionando');
    const mover = (ev) => {
      const w = Math.max(60, Math.min(700, w0 + ev.clientX - x0));
      largRef.current = { ...largRef.current, [chave]: w };
      setLarg(largRef.current);
    };
    const soltar = () => {
      document.removeEventListener('pointermove', mover);
      document.removeEventListener('pointerup', soltar);
      document.body.classList.remove('agset-redimensionando');
      salvarLarg();
    };
    document.addEventListener('pointermove', mover);
    document.addEventListener('pointerup', soltar);
  };
  const restaurarLarg = (chave) => {
    largRef.current = { ...largRef.current, [chave]: LARG_PADRAO[chave] };
    setLarg(largRef.current);
    salvarLarg();
  };

  const carregar = useCallback(async () => {
    try {
      setCarregando(true);
      setErro('');
      const estadoCarteiras = await api('/odontologia-pipeline/etapas');
      setIsAdmin(Boolean(estadoCarteiras.isAdmin));
      const etapasServidor = Array.isArray(estadoCarteiras.etapas)
        ? estadoCarteiras.etapas
        : [];

      ETAPAS_DINAMICAS.clear();
      etapasServidor.forEach((item) => ETAPAS_DINAMICAS.add(item.slug));
      ETAPAS_CORES.clear();
      etapasServidor.forEach((item) => { if (item.cor) ETAPAS_CORES.set(item.slug, item.cor); });
      setEtapasEstado(Object.fromEntries(
        etapasServidor.map((item) => [item.slug, item.estado])
      ));

      setEtapasNomes(Object.fromEntries(
        etapasServidor.map((item) => [item.slug, item.nome])
      ));
      const retorno = await api(`/agenda-configuracoes?setor=${encodeURIComponent(setor)}`);
      const agendasCarregadas = lista(retorno).map((item) => normalizarAgenda(item, setor));
      setAgendas(agendasCarregadas);
      if (primeiraCargaRef.current) setRecarregarTick((n) => n + 1);
      else primeiraCargaRef.current = true;
    } catch (e) {
      setErro(e.message || 'Não foi possível carregar a agenda.');
    } finally { setCarregando(false); }
  }, [setor]);

  useEffect(() => { carregar(); }, [carregar]);

  const etapasComNomes = [
    ...ETAPAS.map((etapa) => ({ ...etapa, nome: etapasNomes[etapa.id] || etapa.nome })),
    ...Object.keys(etapasNomes)
      .filter((slugEtapa) => !ETAPAS.some((etapa) => etapa.id === slugEtapa))
      .map((slugEtapa) => ({ id: slugEtapa, nome: etapasNomes[slugEtapa] })),
  ];

  const etapasAtivas = etapasComNomes.filter(
    (etapa) => !['arquivada', 'lixeira'].includes(etapasEstado[etapa.id])
  );
  const etapasArquivadas = etapasComNomes.filter(
    (etapa) => etapasEstado[etapa.id] === 'arquivada'
  );
  const etapasNaLixeira = etapasComNomes.filter(
    (etapa) => etapasEstado[etapa.id] === 'lixeira'
  );

  const [corEtapa, setCorEtapa] = useState('#4D56D8');

  const fecharMenusEtapa = () => {
    document.querySelectorAll('.agset-stage-menu[open]').forEach((menu) => menu.removeAttribute('open'));
  };

  useEffect(() => {
    const fecharAoClicarFora = (evento) => {
      document.querySelectorAll('.agset-stage-menu[open]').forEach((menu) => {
        if (!menu.contains(evento.target)) menu.removeAttribute('open');
      });
    };
    document.addEventListener('mousedown', fecharAoClicarFora);
    return () => document.removeEventListener('mousedown', fecharAoClicarFora);
  }, []);

  const abrirEdicaoEtapa = (etapa) => {
    if (!isAdmin) return;
    fecharMenusEtapa();
    setEtapaEditando(etapa);
    setNomeEtapa(etapa.nome || '');
    setCorEtapa(ETAPAS_CORES.get(etapa.id) || '#4D56D8');
  };

  const salvarNomeEtapa = async (evento) => {
    evento.preventDefault();

    const nome = nomeEtapa.trim();
    if (!etapaEditando || !nome) return;

    try {
      const retorno = await api(
        `/odontologia-pipeline/etapas/${encodeURIComponent(etapaEditando.id)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ nome, cor: corEtapa })
        }
      );

      setEtapasNomes((atuais) => ({
        ...atuais,
        [etapaEditando.id]: retorno.etapa.nome
      }));
      if (retorno.etapa?.cor) ETAPAS_CORES.set(etapaEditando.id, retorno.etapa.cor);
      setEtapaEditando(null);
      setNomeEtapa('');
      setErro('');
    } catch (e) {
      setErro(e.message || 'Não foi possível renomear a etapa.');
    }
  };

  const [novaEtapaAberta, setNovaEtapaAberta] = useState(false);
  const [nomeNovaEtapa, setNomeNovaEtapa] = useState('');
  const [corNovaEtapa, setCorNovaEtapa] = useState('#4D56D8');
  const [criandoEtapa, setCriandoEtapa] = useState(false);

  const criarEtapa = async (evento) => {
    evento.preventDefault();
    const nome = nomeNovaEtapa.trim();
    if (!nome) return;
    try {
      setCriandoEtapa(true);
      const retorno = await api('/odontologia-pipeline/etapas', { method: 'POST', body: JSON.stringify({ nome, cor: corNovaEtapa }) });
      const nova = retorno.etapa;
      ETAPAS_DINAMICAS.add(nova.slug);
      if (nova.cor) ETAPAS_CORES.set(nova.slug, nova.cor);
      setEtapasNomes((atuais) => ({ ...atuais, [nova.slug]: nova.nome }));
      setEtapasEstado((atuais) => ({ ...atuais, [nova.slug]: nova.estado || 'ativa' }));
      setEtapaAtiva(nova.slug);
      setNovaEtapaAberta(false);
      setNomeNovaEtapa('');
    } catch (e) {
      setErro(e.message || 'Não foi possível criar a carteira.');
    } finally {
      setCriandoEtapa(false);
    }
  };

  const alterarEstadoEtapa = async (etapa, estado) => {
    fecharMenusEtapa();
    if (!isAdmin) return;
    const nomeAcao = estado === 'arquivada' ? 'arquivar' : estado === 'lixeira' ? 'enviar para a Lixeira' : 'restaurar';
    const mensagem = estado === 'ativa' ? `Restaurar a carteira “${etapa.nome}”?` : `Deseja ${nomeAcao} “${etapa.nome}”? Os pacientes e horários serão preservados e a carteira poderá ser restaurada.`;
    if (!window.confirm(mensagem)) return;
    try {
      const retorno = await api(`/odontologia-pipeline/etapas/${encodeURIComponent(etapa.id)}/estado`, { method: 'POST', body: JSON.stringify({ estado }) });
      setEtapasEstado((atual) => ({ ...atual, [etapa.id]: retorno.etapa.estado }));
      if (estado !== 'ativa' && etapaAtiva === etapa.id) setEtapaAtiva('novos_associados');
      setErro('');
    } catch (e) { setErro(e.message || 'Não foi possível atualizar a carteira.'); }
  };

  const atualizar = async (paciente, campos) => {
    setPacientes((atuais) => atuais.map((item) => item.id === paciente.id ? { ...item, ...campos } : item));
    if (!String(paciente.id).includes('-') || paciente.agendaId) {
      try { await api(`/agenda-horarios/${paciente.id}`, { method: 'PUT', body: JSON.stringify(campos) }); }
      catch (e) { setErro(e.message); carregar(); }
    }
  };

  const salvarExtra = (item, campo, valor) => atualizar(item, {
    dados_extras: { ...(item.dados_extras || {}), etapa: item.etapa, [campo]: valor },
  });

  const salvarDataEnvio = (item, valor) => {
    const s = texto(valor).trim();
    if (!s) return;
    const m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/.exec(s);
    if (!m) { setErro('Data inválida. Use o formato dd/mm/aaaa.'); return; }
    let ano = Number(m[3]);
    if (ano < 100) ano += 2000;
    const mes = Number(m[2]);
    const dia = Number(m[1]);
    const conferida = new Date(ano, mes - 1, dia);
    if (ano < 1900 || ano > 2100 || conferida.getMonth() !== mes - 1 || conferida.getDate() !== dia) {
      setErro('Data inválida. Use o formato dd/mm/aaaa.');
      return;
    }
    const iso = `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    atualizar(item, { data: iso, dados_extras: { ...(item.dados_extras || {}), etapa: item.etapa, data_envio: iso } });
  };

  const filtrados = pacientes;

  const contagem = (id) => contagensEtapa[id] || 0;
  const statusNome = (valor) => STATUS.find(([id]) => id === valor)?.[1] || valor;
  const selecionadosAtuais = pacientes.filter((item) => selecionados.includes(item.id));

  const moverSelecionados = async (etapa) => {
    if (!etapa || !selecionadosAtuais.length) return;
    await Promise.all(selecionadosAtuais.map((item) => atualizar(item, { dados_extras: { ...(item.dados_extras || {}), etapa }, etapa })));
    setSelecionados([]);
    setRecarregarTick((n) => n + 1);
  };

  const excluirSelecionados = async () => {
    if (!selecionadosAtuais.length || !window.confirm(`Enviar ${selecionadosAtuais.length} registro(s) para a lixeira?`)) return;
    try {
      await Promise.all(selecionadosAtuais.filter((item) => item.id && item.agendaId).map((item) => api(`/agenda-horarios/${item.id}`, { method: 'DELETE' })));
      setPacientes((atuais) => atuais.filter((item) => !selecionados.includes(item.id)));
      setRecarregarTick((n) => n + 1);
      setSelecionados([]);
    } catch (e) { setErro(e.message); }
  };

  const abrirHistorico = async (item) => {
    try {
      const retorno = await api(`/agenda-horarios/${item.id}/historico`);
      setHistoricoModal({ item, eventos: Array.isArray(retorno) ? retorno : (retorno.items || retorno.data || []) });
    } catch (_) {
      setHistoricoModal({ item, eventos: [{ campo: 'registro', descricao: 'Registro atual consultado', data: new Date().toISOString(), valor: item.status }] });
    }
  };

  const copiarSelecionados = () => {
    if (!selecionadosAtuais.length) return;
    setCopiado(selecionadosAtuais.map(({ nome, contato, status, etapa, data, observacao }) => ({ nome, contato, status, etapa, data, observacao })));
  };

  const colarCopiados = async () => {
    const agenda = agendas[0];
    if (!agenda || !copiado?.length) return;
    try {
      const criados = await Promise.all(copiado.map((item) => api(`/agenda-configuracoes/${agenda.id}/horarios`, { method: 'POST', body: JSON.stringify({ horario: '', paciente: item.nome, contato: item.contato, observacao: item.observacao, status: item.status, dados_extras: { etapa: item.etapa } }) })));
      setRecarregarTick((n) => n + 1);
    } catch (e) { setErro(e.message); }
  };

  const criarPaciente = async (e) => {
    e.preventDefault();
    const agenda = agendas[0];
    if (!agenda) { setErro('Crie uma agenda antes de cadastrar pacientes.'); return; }
    try {
      const criado = await api(`/agenda-configuracoes/${agenda.id}/horarios`, {
        method: 'POST',
        body: JSON.stringify({ horario: novo.horario || agenda.hora_inicio || agenda.inicio || '09:00', paciente: novo.nome, contato: novo.contato, observacao: novo.observacao, status: novo.status, data: novo.data || agenda.data || undefined, dados_extras: { etapa: novo.etapa } })
      });
      setRecarregarTick((n) => n + 1);
      setEtapaAtiva(novo.etapa);
      setNovo({ nome: '', contato: '', status: 'aguardando_contato', etapa: 'novos_associados', data: '', horario: '', observacao: '' });
      setModalNovo(false);
    } catch (e2) { setErro(e2.message); }
  };

  const exportar = async () => {
    let filtrados = [];
    try {
      setCarregandoPagina(true);
      filtrados = await buscarTodosRegistros({ etapa: etapaAtiva, status: filtroStatus, busca, data: dataBusca, ordem: ordenacao, direcao: ordemInversa ? 'desc' : 'asc' });
    } catch (e) {
      setErro(e.message || 'Não foi possível exportar.');
      return;
    } finally {
      setCarregandoPagina(false);
    }
    const linhas = [['Nome', 'Telefone', 'Tratamentos', 'Valor/total', 'Dentista', 'Virou Ficha', 'Status', 'Data do envio', 'Observações'], ...filtrados.map((item) => [item.nome, item.contato, extraPaciente(item, 'tratamentos'), extraPaciente(item, 'valor_total'), extraPaciente(item, 'dentista'), ({ sim: 'Sim', nao: 'Não' })[extraPaciente(item, 'virou_ficha')] || '', statusNome(item.status), dataBR(item.data), item.observacao])];
    const csv = '\uFEFF' + linhas.map((linha) => linha.map((valor) => `"${texto(valor).replace(/"/g, '""')}"`).join(';')).join('\n');
    const link = document.createElement('a'); link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' })); link.download = `agenda-odontologia-${etapaAtiva}.csv`; link.click();
  };

  const [importacao, setImportacao] = useState(null);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [menuMaisAberto, setMenuMaisAberto] = useState(false);
  useEffect(() => {
    if (!menuMaisAberto) return undefined;
    const fechar = (e) => { if (!e.target.closest || !e.target.closest('.agset-kebab')) setMenuMaisAberto(false); };
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, [menuMaisAberto]);
  const [pagina, setPagina] = useState(1);
  useEffect(() => { setPagina(1); }, [etapaAtiva, filtroStatus, dataBusca]);
  useEffect(() => {
    const espera = setTimeout(() => { setBuscaServidor(busca); setPagina(1); }, 350);
    return () => clearTimeout(espera);
  }, [busca]);
  useEffect(() => { setPagina(1); }, [ordenacao, ordemInversa]);
  useEffect(() => { setSelecionados([]); }, [etapaAtiva, pagina]);
  useEffect(() => {
    let cancelado = false;
    const numero = requisicaoRef.current + 1;
    requisicaoRef.current = numero;
    setCarregandoPagina(true);
    buscarRegistros({ etapa: etapaAtiva, pagina, status: filtroStatus, busca: buscaServidor, data: dataBusca, ordem: ordenacao, direcao: ordemInversa ? 'desc' : 'asc' })
      .then((r) => {
        if (cancelado || numero !== requisicaoRef.current) return;
        if (!r.rows.length && pagina > 1 && r.total > 0) { setPagina(Math.max(1, r.totalPages)); return; }
        setPacientes(r.rows);
        setTotalRegistros(r.total);
        if (!filtroStatus) setContagensEtapa(r.counts);
        setCarregandoPagina(false);
      })
      .catch((e) => {
        if (cancelado || numero !== requisicaoRef.current) return;
        setErro(e.message || 'Não foi possível carregar os registros.');
        setCarregandoPagina(false);
      });
    return () => { cancelado = true; };
  }, [etapaAtiva, pagina, filtroStatus, buscaServidor, dataBusca, ordenacao, ordemInversa, recarregarTick]);

  const etapaDoPaciente = (p) => p?.etapa ?? p?.dados_extras?.etapa;

  const listaParaImportar = (dados) => {
    if (!dados) return [];
    const existentes = new Set(
      (dados.existentes || []).filter((p) => etapaDoPaciente(p) === dados.etapa).map((p) => chavePaciente(p.nome, p.contato))
    );
    return dados.validas.filter((l) => !dados.ignorarDuplicados || !existentes.has(chavePaciente(l.nome, l.contato)));
  };

  const importar = async (evento) => {
    const arquivoEscolhido = evento.target.files?.[0];
    evento.target.value = '';
    if (!arquivoEscolhido) return;
    try {
      const lido = await lerPlanilhaPacientes(arquivoEscolhido);
      if (!lido.validas.length) { setErro(`Não encontrei linhas com nome de paciente em “${arquivoEscolhido.name}”.`); return; }
      let existentes = [];
      try { setCarregandoPagina(true); existentes = await buscarTodosRegistros({}); } finally { setCarregandoPagina(false); }
      setImportacao({
        existentes,
        arquivo: arquivoEscolhido.name,
        ...lido,
        etapa: etapaAtiva,
        ignorarDuplicados: true,
        atualizarExistentes: true,
        executando: false,
        feitos: 0,
        total: 0,
        falhas: 0,
        concluido: false,
        importados: 0,
      });
    } catch (e) {
      setErro(`Não foi possível ler a planilha: ${e.message}`);
    }
  };

  const executarImportacao = async () => {
    const agenda = agendas[0];
    if (!agenda) { setErro('Crie uma agenda antes de importar pacientes.'); return; }
    const etapa = importacao.etapa;
    const linhas = listaParaImportar(importacao);
    const existentesMapa = new Map(
      (importacao.existentes || []).filter((p) => etapaDoPaciente(p) === etapa).map((p) => [chavePaciente(p.nome, p.contato), p])
    );
    const paraAtualizar = importacao.atualizarExistentes
      ? importacao.validas
        .map((l) => ({ l, p: existentesMapa.get(chavePaciente(l.nome, l.contato)) }))
        .filter((x) => x.p && /^\d+$/.test(String(x.p.id)) && Object.keys(extrasImportacao(x.l)).length)
      : [];
    if (!linhas.length && !paraAtualizar.length) return;
    setImportacao((atual) => ({ ...atual, executando: true, feitos: 0, total: linhas.length + paraAtualizar.length, falhas: 0 }));

    const criados = [];
    let falhas = 0;
    for (let i = 0; i < linhas.length; i += 5) {
      const lote = linhas.slice(i, i + 5);
      const resultados = await Promise.all(lote.map(async (l) => {
        try {
          const criado = await api(`/agenda-configuracoes/${agenda.id}/horarios`, {
            method: 'POST',
            body: JSON.stringify({
              horario: l.horario || agenda.hora_inicio || agenda.inicio || '09:00',
              paciente: l.nome,
              contato: l.contato,
              observacao: l.observacao,
              status: l.status,
              data: l.data || agenda.data || undefined,
              etapa,
              dados_extras: { etapa, ...extrasImportacao(l) },
            }),
          });
          if (criado?.id != null && ((criado?.dados_extras?.etapa ?? criado?.etapa) !== etapa || Object.entries(extrasImportacao(l)).some(([k, v]) => (criado?.dados_extras || {})[k] !== v))) {
            await api(`/agenda-horarios/${criado.id}`, { method: 'PUT', body: JSON.stringify({ dados_extras: { ...(criado.dados_extras || {}), etapa, ...extrasImportacao(l) }, etapa }) });
          }
          return { criado, l };
        } catch (_) {
          falhas += 1;
          return null;
        }
      }));
      resultados.filter(Boolean).forEach((r) => criados.push(r));
      setImportacao((atual) => ({ ...atual, feitos: Math.min(linhas.length, i + lote.length), falhas }));
    }

    setPacientes((atuais) => [
      ...atuais,
      ...criados.map((r, k) => normalizarPaciente({
        ...r.criado,
        nome: r.l.nome,
        contato: r.l.contato,
        observacao: r.l.observacao,
        status: r.l.status,
        data: r.l.data,
        etapa,
        dados_extras: { ...(r.criado?.dados_extras || {}), etapa, ...extrasImportacao(r.l) },
      }, agenda, atuais.length + k)),
    ]);
    setEtapaAtiva(etapa);
    const atualizadosMapa = new Map();
    for (let j = 0; j < paraAtualizar.length; j += 5) {
      const lote = paraAtualizar.slice(j, j + 5);
      await Promise.all(lote.map(async ({ l, p }) => {
        const novosExtras = { ...(p.dados_extras || {}), etapa, ...extrasImportacao(l) };
        try {
          await api(`/agenda-horarios/${p.id}`, { method: 'PUT', body: JSON.stringify({ dados_extras: novosExtras }) });
          atualizadosMapa.set(String(p.id), novosExtras);
        } catch (_) {
          falhas += 1;
        }
      }));
      setImportacao((atual) => ({ ...atual, feitos: Math.min(linhas.length + paraAtualizar.length, linhas.length + j + lote.length), falhas }));
    }
    if (atualizadosMapa.size) {
      setPacientes((atuais) => atuais.map((item) => (atualizadosMapa.has(String(item.id)) ? { ...item, dados_extras: atualizadosMapa.get(String(item.id)) } : item)));
    }

    setRecarregarTick((n) => n + 1);
    setImportacao((atual) => ({ ...atual, executando: false, concluido: true, importados: criados.length, atualizados: atualizadosMapa.size, falhas }));
  };

  const etapasCarouselRef = useRef(null);
  const [etapasComOverflow, setEtapasComOverflow] = useState(false);
  const [cardsRecolhidos, setCardsRecolhidos] = useState(() => {
    try { const m = localStorage.getItem('agset-cards-manual'); if (m === '1' || m === '0') return m === '1'; } catch (_) { /* sem storage */ }
    try { return !!(window.matchMedia && window.matchMedia('(max-width: 1599px)').matches); } catch (_) { return false; }
  });
  const [menuEtapasAberto, setMenuEtapasAberto] = useState(false);
  const menuEtapasRef = useRef(null);
  const [posMenuEtapas, setPosMenuEtapas] = useState(null);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 1599px)');
    const seguir = (e) => {
      let m = null;
      try { m = localStorage.getItem('agset-cards-manual'); } catch (_) { /* sem storage */ }
      if (m === '1' || m === '0') return;
      setCardsRecolhidos(e.matches);
    };
    if (mq.addEventListener) mq.addEventListener('change', seguir); else mq.addListener(seguir);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', seguir); else mq.removeListener(seguir); };
  }, []);
  useEffect(() => {
    if (!menuEtapasAberto) return undefined;
    const fechar = (e) => { if (menuEtapasRef.current && e.target && menuEtapasRef.current.contains(e.target)) return; setMenuEtapasAberto(false); };
    window.addEventListener('resize', fechar);
    window.addEventListener('scroll', fechar, true);
    return () => { window.removeEventListener('resize', fechar); window.removeEventListener('scroll', fechar, true); };
  }, [menuEtapasAberto]);
  const alternarCards = () => {
    setCardsRecolhidos((v) => {
      const novo = !v;
      try { localStorage.setItem('agset-cards-manual', novo ? '1' : '0'); } catch (_) { /* sem storage */ }
      return novo;
    });
    setMenuEtapasAberto(false);
  };
  useEffect(() => {
    if (!menuEtapasAberto) return undefined;
    const fechar = (e) => { if (menuEtapasRef.current && !menuEtapasRef.current.contains(e.target)) setMenuEtapasAberto(false); };
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, [menuEtapasAberto]);


  useEffect(() => {
    const elemento = etapasCarouselRef.current;
    if (!elemento) return undefined;

    const medirOverflow = () => {
      setEtapasComOverflow(
        elemento.scrollWidth > elemento.clientWidth + 2
      );
    };

    medirOverflow();

    const observador = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(medirOverflow)
      : null;

    if (observador) observador.observe(elemento);
    window.addEventListener('resize', medirOverflow);

    return () => {
      observador?.disconnect();
      window.removeEventListener('resize', medirOverflow);
    };
  }, [etapasAtivas.length, cardsRecolhidos]);

  const deslocarEtapas = (direcao) => {
    const elemento = etapasCarouselRef.current;
    if (!elemento) return;

    elemento.scrollBy({
      left: direcao * Math.max(260, Math.round(elemento.clientWidth * 0.72)),
      behavior: 'smooth'
    });
  };

  const POR_PAGINA = 30;
  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const paginados = filtrados;

  return <main className={`agset-pipeline-page ${ampliado ? 'agset-pipeline-expanded' : ''} ${cardsRecolhidos ? 'agset-cards-compact' : ''} ${carregandoPagina ? 'agset-page-loading' : ''}`}>
    <header className="agset-pipeline-header">
      <div className="agset-brand-block"><span className="agset-pipeline-kicker">ODONTO SOULIDARI · GESTÃO DE BASES</span><h1>Agendamentos</h1><p>Organize pacientes, tratamentos e movimentações da odontologia.</p></div><label className="agset-global-search"><Search size={15} /><input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar paciente em todas as categorias..." /></label>
      <div className="agset-pipeline-header-actions">{isAdmin && <button type="button" className="agset-pipeline-button agset-btn-nova-carteira" onClick={() => { setNomeNovaEtapa(''); setCorNovaEtapa('#4D56D8'); setNovaEtapaAberta(true); }}><Plus size={15} /> Nova carteira</button>}<label className="agset-pipeline-button ghost"><Upload size={15} /> Importar Excel/CSV<input type="file" accept=".csv,.xlsx,.xls" hidden onChange={importar} /></label><button className="agset-pipeline-button primary" onClick={() => setModalNovo(true)}><Plus size={16} /> Novo paciente</button><div className="agset-kebab">
      <button type="button" className="agset-kebab-toggle" aria-label="Mais opções" title="Mais opções" onClick={() => setMenuMaisAberto((v) => !v)}><MoreVertical size={16} /></button>
      {menuMaisAberto && <div className="agset-kebab-menu" onClick={() => setMenuMaisAberto(false)}>{isAdmin && <button type="button" className="agset-kebab-item" onClick={async () => { const atual = etapasComNomes.find((x) => x.id === etapaAtiva); if (!atual || !window.confirm(`Arquivar a etapa “${atual.nome}”? Os pacientes ficam guardados e ela pode ser restaurada.`)) return; const proxima = etapasAtivas.find((x) => x.id !== atual.id); await alterarEstadoEtapa(atual, 'arquivada'); if (proxima) setEtapaAtiva(proxima.id); }}><Archive size={15} /> Arquivar etapa atual</button>}
{isAdmin && <button type="button" className="agset-kebab-item" onClick={async () => { const atual = etapasComNomes.find((x) => x.id === etapaAtiva); if (!atual || !window.confirm(`Mandar a etapa “${atual.nome}” para a lixeira? Ela pode ser restaurada depois.`)) return; const proxima = etapasAtivas.find((x) => x.id !== atual.id); await alterarEstadoEtapa(atual, 'lixeira'); if (proxima) setEtapaAtiva(proxima.id); }}><Trash2 size={15} /> Excluir etapa atual</button>}
{isAdmin && <button type="button" className="agset-kebab-item" onClick={() => setLixeira(true)}><RotateCcw size={15} /> Arquivadas e lixeira</button>}<button type="button" className="agset-kebab-item" onClick={exportar}><Download size={15} /> Baixar</button></div>}
    </div></div>
    </header>

    <div className="agset-cards-toggle-row"><button type="button" className="agset-cards-toggle" onClick={alternarCards} aria-expanded={!cardsRecolhidos} title={cardsRecolhidos ? "Mostrar cards" : "Recolher cards"} aria-label={cardsRecolhidos ? "Mostrar cards" : "Recolher cards"}>{cardsRecolhidos ? "▾" : "▴"}</button></div>
    {!cardsRecolhidos && (
    <div className={`agset-stage-row ${etapasComOverflow ? "has-overflow" : "sem-overflow"}`}>
      {etapasComOverflow && (
      <button
        type="button"
        className="agset-stage-arrow agset-stage-arrow-left"
        aria-label="Mostrar etapas anteriores"
        title="Mostrar etapas anteriores"
        onClick={() => deslocarEtapas(-1)}
      >
        ‹
      </button>
      )}

      <div className="agset-stage-viewport" ref={etapasCarouselRef} style={{ display: "block", flex: "1 1 auto", minWidth: 0, width: "auto", overflow: "hidden" }}>
        <section className="agset-stage-cards" style={{ display: "flex", flexDirection: "row", flexWrap: "nowrap", alignItems: "stretch", gap: 12, width: "max-content", minWidth: 0 }}>
          {etapasAtivas.map((etapa, index) => (
            <div className="agset-stage-card-slot" key={etapa.id} style={{ display: "block", flex: "0 0 180px", width: 180, minWidth: 180, maxWidth: 180 }}>
              <button
                type="button"
                className={`agset-etapa-card ${etapaAtiva === etapa.id ? 'active' : ''} ${ETAPAS_CORES.get(etapa.id) ? 'tem-cor' : ''}`} style={ETAPAS_CORES.get(etapa.id) ? { '--etapa-cor': ETAPAS_CORES.get(etapa.id) } : undefined}
                onClick={() => setEtapaAtiva(etapa.id)}
              >
                <small>{String(index + 1).padStart(2, '0')}</small>
                <strong>{etapa.nome}</strong>
                <b>{contagem(etapa.id)}</b>
              </button>

              {isAdmin && (
                <details className="agset-stage-menu">
                  <summary
                    title="Gerenciar etapa"
                    aria-label="Gerenciar etapa"
                  >
                    <MoreHorizontal size={15} />
                  </summary>

                  <div className="agset-stage-menu-popover">
                    <button
                      type="button"
                      className="agset-pipeline-button ghost"
                      onClick={() => abrirEdicaoEtapa(etapa)}
                    >
                      <Pencil size={13} /> Editar
                    </button>

                    <button
                      type="button"
                      className="agset-pipeline-button ghost"
                      onClick={() => alterarEstadoEtapa(etapa, 'arquivada')}
                    >
                      <Archive size={13} /> Arquivar
                    </button>

                    <button
                      type="button"
                      className="agset-pipeline-button ghost"
                      onClick={() => alterarEstadoEtapa(etapa, 'lixeira')}
                    >
                      <Trash2 size={13} /> Excluir
                    </button>
                  </div>
                </details>
              )}
            </div>
          ))}
        </section>
      </div>

      {etapasComOverflow && (
      <button
        type="button"
        className="agset-stage-arrow agset-stage-arrow-right"
        aria-label="Mostrar próximas etapas"
        title="Mostrar próximas etapas"
        onClick={() => deslocarEtapas(1)}
      >
        ›
      </button>
      )}
    </div>
    )}
    {cardsRecolhidos ? (
    <nav className="agset-stage-tabs agset-stage-tabs-drop"><div className="agset-stage-dropdown" ref={menuEtapasRef}>
      <button type="button" className="active agset-stage-dropdown-btn" aria-haspopup="listbox" aria-expanded={menuEtapasAberto} onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setPosMenuEtapas({ left: r.left, top: r.bottom + 6 }); setMenuEtapasAberto((v) => !v); }}><i />{etapasAtivas.find((e) => e.id === etapaAtiva)?.nome || 'Etapa'}<span>{contagem(etapaAtiva)}</span><em>▾</em></button>
      {menuEtapasAberto && posMenuEtapas && <div className="agset-etapa-lista" role="listbox" style={{ position: 'fixed', left: posMenuEtapas.left, top: posMenuEtapas.top }}>{etapasAtivas.map((etapa) => <div role="option" tabIndex={0} aria-selected={etapaAtiva === etapa.id} key={etapa.id} className={`agset-etapa-opcao ${etapaAtiva === etapa.id ? 'selecionada' : ''}`} onClick={() => { setEtapaAtiva(etapa.id); setMenuEtapasAberto(false); }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEtapaAtiva(etapa.id); setMenuEtapasAberto(false); } }}><span className="agset-etapa-opcao-nome">{etapa.nome}</span><span className="agset-etapa-opcao-qtd">{contagem(etapa.id)}</span></div>)}</div>}
    </div></nav>
    ) : (
    <nav className="agset-stage-tabs">{etapasAtivas.map((etapa) => <button key={etapa.id} className={etapaAtiva === etapa.id ? 'active' : ''} onClick={() => setEtapaAtiva(etapa.id)}><i />{etapa.nome}<span>{contagem(etapa.id)}</span></button>)}</nav>
    )}

    <section className="agset-pipeline-panel">
      <div className="agset-pipeline-panel-title"><div><span>Odontologia</span><h2>{etapasComNomes.find((item) => item.id === etapaAtiva)?.nome}</h2><p>{totalRegistros} registro(s) nesta etapa</p></div><div className={`agset-toolbar ${filtrosAbertos || selecionadosAtuais.length > 0 ? 'is-aberto' : 'is-fechado'}`}>{selecionadosAtuais.length > 0 && <><span className="agset-selected-count">{selecionadosAtuais.length} selecionado(s)</span><button className="agset-tool-action" onClick={copiarSelecionados}><Copy size={13} /> Copiar</button><select className="agset-move-select" value="" onChange={(e) => moverSelecionados(e.target.value)}><option value="">Mover para...</option>{etapasAtivas.map((etapa) => <option key={etapa.id} value={etapa.id}>{etapa.nome}</option>)}</select><button className="agset-tool-action danger" onClick={excluirSelecionados}><Trash2 size={13} /></button>{copiado && <button className="agset-tool-action" onClick={colarCopiados}>Colar</button>}</>}<label><Filter size={14} /><select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}><option value="">Todos os status</option>{STATUS.map(([valor, nome]) => <option key={valor} value={valor}>{nome}</option>)}</select></label><div className="agset-date-search"><DataBuscaBR value={dataBusca} onChange={setDataBusca} /></div><select aria-label="Ordenar por" value={ordenacao} onChange={(e) => setOrdenacao(e.target.value)}><option value="nome">Nome</option><option value="status">Status</option><option value="data">Data</option></select><button className="agset-tool-action agset-order-button" onClick={() => setOrdemInversa((v) => !v)}>{ordemInversa ? "↓" : "↑"}</button><button className="agset-tool-action" onClick={copiarSelecionados} disabled={!selecionadosAtuais.length}><Copy size={13} /> Copiar</button>{copiado && <button className="agset-tool-action" onClick={colarCopiados}>Colar</button>}<button className="agset-tool-action" onClick={exportar}><FileDown size={13} /> Excel/CSV</button><button type="button" className="agset-tool-action agset-tool-toggle" onClick={() => setFiltrosAbertos((v) => !v)} title={filtrosAbertos ? 'Ocultar filtros' : 'Mostrar filtros'}><Filter size={13} /> Filtros{(filtroStatus || dataBusca) && !filtrosAbertos && <span className="agset-filtro-ativo" />} <ChevronDown size={13} className={filtrosAbertos ? 'agset-chevron-aberto' : ''} /></button><button className="agset-tool-action agset-tool-ampliar" onClick={() => setAmpliado((v) => !v)}><Maximize2 size={13} /> {ampliado ? 'Reduzir' : 'Ampliar'}</button></div></div>
      {erro && <div className="agset-pipeline-error">{erro}<button onClick={() => setErro('')}><X size={14} /></button></div>}
      <div className="agset-pipeline-table-wrap"><table className="agset-pipeline-table agset-pipeline-resizable" style={{ width: 44 + COLS_PIPELINE.reduce((total, [chave]) => total + larg[chave], 0) }}><colgroup><col style={{ width: 44 }} />{COLS_PIPELINE.map(([chave]) => <col key={chave} style={{ width: larg[chave] }} />)}</colgroup><thead><tr><th><input type="checkbox" checked={paginados.length > 0 && paginados.every((item) => selecionados.includes(item.id))} onChange={(e) => setSelecionados((atuais) => e.target.checked ? Array.from(new Set([...atuais, ...paginados.map((item) => item.id)])) : atuais.filter((id) => !paginados.some((item) => item.id === id)))} /></th>{COLS_PIPELINE.map(([chave, rotulo]) => <th key={chave} className="agset-th-resizable">{(chave === 'contato' || chave === 'telefone') ? <span className="agset-telefone-cabecalho"><span>{rotulo}</span><label title="Habilitar telefone clicável pelo WhatsApp"><input type="checkbox" checked={whatsappTelefoneAtivo} onChange={(e) => alternarWhatsappTelefone(e.target.checked)} /> WhatsApp</label></span> : rotulo}<span className="agset-col-resize" title="Arraste para ajustar a largura (duplo clique restaura)" onPointerDown={(e) => iniciarResize(e, chave)} onDoubleClick={() => restaurarLarg(chave)} /></th>)}</tr></thead><tbody>{carregando ? <tr><td colSpan={COLS_PIPELINE.length + 1} className="agset-pipeline-empty">Carregando...</td></tr> : totalRegistros === 0 ? <tr><td colSpan={COLS_PIPELINE.length + 1} className="agset-pipeline-empty">Nenhum paciente nesta etapa.</td></tr> : paginados.map((item) => <tr key={item.id}><td><input type="checkbox" checked={selecionados.includes(item.id)} onChange={(e) => setSelecionados((atuais) => e.target.checked ? [...atuais, item.id] : atuais.filter((id) => id !== item.id))} /></td><td><strong><CampoEditavel valor={item.nome} onSave={(valor) => atualizar(item, { paciente: valor, nome: valor })} /></strong></td><td className={whatsappTelefoneAtivo && item.contato ? 'agset-telefone-clicavel' : ''}>
  {whatsappTelefoneAtivo && item.contato ? (
    <div className="agset-telefone-link-wrap">
      <button
        type="button"
        className="agset-telefone-whatsapp"
        title="Enviar mensagem pelo WhatsApp"
        onClick={() => abrirWhatsappTelefone(item.contato)}
      >
        {item.contato}
      </button>
      <CampoEditavel
        valor={item.contato}
        onSave={(valor) => atualizar(item, { contato: valor })}
        placeholder="Telefone"
      />
    </div>
  ) : (
    <CampoEditavel
      valor={item.contato}
      onSave={(valor) => atualizar(item, { contato: valor })}
      placeholder="Telefone"
    />
  )}
</td><td><CampoEditavel valor={extraPaciente(item, 'tratamentos')} onSave={(valor) => salvarExtra(item, 'tratamentos', valor)} placeholder="Tratamentos" /></td><td><CampoEditavel valor={extraPaciente(item, 'valor_total')} onSave={(valor) => salvarExtra(item, 'valor_total', valor)} placeholder="R$ 0,00" /></td><td><CampoEditavel valor={extraPaciente(item, 'dentista')} onSave={(valor) => salvarExtra(item, 'dentista', valor)} placeholder="Dentista" /></td><td><select className="agset-ficha-select" value={extraPaciente(item, 'virou_ficha')} onChange={(e) => salvarExtra(item, 'virou_ficha', e.target.value)}><option value="">—</option><option value="sim">Sim</option><option value="nao">Não</option></select></td><td><select className={`agset-pipeline-status status-${item.status}`} value={item.status} onChange={(e) => atualizar(item, { status: e.target.value })}>{STATUS.map(([valor, nome]) => <option key={valor} value={valor}>{nome}</option>)}</select></td><td><CampoEditavel valor={dataBR(extraPaciente(item, 'data_envio') || item.data)} onSave={(valor) => salvarDataEnvio(item, valor)} placeholder="dd/mm/aaaa" /></td><td><CampoEditavel valor={item.observacao} onSave={(valor) => atualizar(item, { observacao: valor })} placeholder="Adicionar observação" /></td><td><div className="agset-pipeline-actions"><select className="agset-row-move" value="" title="Mover para outra etapa" onChange={(e) => { if (e.target.value) atualizar(item, { dados_extras: { ...(item.dados_extras || {}), etapa: e.target.value }, etapa: e.target.value }); e.target.value = ''; }}><option value="">Mover</option>{etapasAtivas.map((etapa) => <option key={etapa.id} value={etapa.id}>{etapa.nome}</option>)}</select><button title="Ver histórico" onClick={() => abrirHistorico(item)}><History size={15} /></button></div></td></tr>)}</tbody></table></div>
      {totalRegistros > POR_PAGINA && <div className="agset-paginacao">
        <span>Mostrando {(paginaAtual - 1) * POR_PAGINA + 1}–{Math.min(paginaAtual * POR_PAGINA, totalRegistros)} de {totalRegistros}</span>
        <div>
          <button type="button" disabled={paginaAtual === 1} onClick={() => setPagina(1)} title="Primeira página">«</button>
          <button type="button" disabled={paginaAtual === 1} onClick={() => setPagina(paginaAtual - 1)}>‹ Anterior</button>
          <span className="agset-paginacao-atual">Página {paginaAtual} de {totalPaginas}</span>
          <button type="button" disabled={paginaAtual === totalPaginas} onClick={() => setPagina(paginaAtual + 1)}>Próxima ›</button>
          <button type="button" disabled={paginaAtual === totalPaginas} onClick={() => setPagina(totalPaginas)} title="Última página">»</button>
        </div>
      </div>}
      <footer className="agset-pipeline-footer"><span><FileSpreadsheet size={14} /> Base odontológica · altere os campos diretamente na célula</span><span>Atualização automática ativa</span></footer>
    </section>

    {etapaEditando && <div className="agset-pipeline-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && setEtapaEditando(null)}>
      <form className="agset-pipeline-modal agset-etapa-edicao" onSubmit={salvarNomeEtapa}>
        <div className="agset-pipeline-modal-head">
          <div>
            <h2>Editar carteira</h2>
            <p>Altere o nome e a cor da carteira.</p>
          </div>
          <button type="button" onClick={() => setEtapaEditando(null)} aria-label="Fechar">
            <X size={18} />
          </button>
        </div>
        <label>
          Nome da carteira
          <input
            autoFocus
            maxLength={150}
            value={nomeEtapa}
            onChange={(e) => setNomeEtapa(e.target.value)}
          />
        </label>
        <div className="agset-carteira-cores">
          <span>Cor da carteira</span>
          <div>
            {CORES_CARTEIRA.map((cor) => <button key={cor} type="button" className={`agset-carteira-cor${corEtapa === cor ? ' is-selected' : ''}`} style={{ background: cor }} onClick={() => setCorEtapa(cor)} aria-label={`Cor ${cor}`}>{corEtapa === cor ? '✓' : ''}</button>)}
            <label className={`agset-carteira-cor-custom${!CORES_CARTEIRA.includes(corEtapa) ? ' is-selected' : ''}`} title="Escolher outra cor" style={!CORES_CARTEIRA.includes(corEtapa) ? { background: corEtapa } : undefined}>
              <input type="color" value={corEtapa} onChange={(e) => setCorEtapa(e.target.value.toUpperCase())} aria-label="Escolher outra cor" /><span>+</span>
            </label>
          </div>
        </div>
        <div className="agset-pipeline-modal-foot">
          <button type="button" className="agset-pipeline-button ghost" onClick={() => setEtapaEditando(null)}>
            Cancelar
          </button>
          <button type="submit" className="agset-pipeline-button primary" disabled={!nomeEtapa.trim()}>
            Salvar alterações
          </button>
        </div>
      </form>
    </div>}

    {novaEtapaAberta && <div className="agset-pipeline-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !criandoEtapa && setNovaEtapaAberta(false)}>
      <form className="agset-pipeline-modal agset-etapa-edicao" onSubmit={criarEtapa}>
        <div className="agset-pipeline-modal-head">
          <div><h2>Nova carteira</h2><p>Crie uma nova carteira para organizar pacientes.</p></div>
          <button type="button" onClick={() => setNovaEtapaAberta(false)} aria-label="Fechar"><X size={18} /></button>
        </div>
        <label>Nome da carteira
          <input autoFocus maxLength={150} value={nomeNovaEtapa} onChange={(e) => setNomeNovaEtapa(e.target.value)} placeholder="Ex.: Carteira Dra. Julia" />
        </label>
        <div className="agset-carteira-cores">
          <span>Cor da carteira</span>
          <div>
            {CORES_CARTEIRA.map((cor) => <button key={cor} type="button" className={`agset-carteira-cor${corNovaEtapa === cor ? ' is-selected' : ''}`} style={{ background: cor }} onClick={() => setCorNovaEtapa(cor)} aria-label={`Cor ${cor}`}>{corNovaEtapa === cor ? '✓' : ''}</button>)}
            <label className={`agset-carteira-cor-custom${!CORES_CARTEIRA.includes(corNovaEtapa) ? ' is-selected' : ''}`} title="Escolher outra cor" style={!CORES_CARTEIRA.includes(corNovaEtapa) ? { background: corNovaEtapa } : undefined}>
              <input type="color" value={corNovaEtapa} onChange={(e) => setCorNovaEtapa(e.target.value.toUpperCase())} aria-label="Escolher outra cor" /><span>+</span>
            </label>
          </div>
        </div>
        <div className="agset-pipeline-modal-foot">
          <button type="button" className="agset-pipeline-button ghost" onClick={() => setNovaEtapaAberta(false)} disabled={criandoEtapa}>Cancelar</button>
          <button type="submit" className="agset-pipeline-button primary" disabled={!nomeNovaEtapa.trim() || criandoEtapa}>{criandoEtapa ? 'Criando…' : 'Criar carteira'}</button>
        </div>
      </form>
    </div>}
    {lixeira && <div className="agset-pipeline-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && setLixeira(false)}><section className="agset-pipeline-modal"><div className="agset-pipeline-modal-head"><div><h2>Carteiras arquivadas e na Lixeira</h2><p>As carteiras e os pacientes permanecem preservados. Somente o Admin pode restaurar ou alterar o estado.</p></div><button type="button" onClick={() => setLixeira(false)} aria-label="Fechar"><X size={18} /></button></div><h3>Arquivadas</h3>{etapasArquivadas.length ? etapasArquivadas.map((etapa) => <div key={etapa.id} className="agset-stage-recovery-row"><strong>{etapa.nome}</strong><span>{contagem(etapa.id)} paciente(s) preservado(s)</span>{isAdmin && <button type="button" className="agset-pipeline-button ghost" onClick={() => alterarEstadoEtapa(etapa, 'ativa')}><RotateCcw size={14} /> Restaurar</button>}</div>) : <p>Nenhuma carteira arquivada.</p>}<h3>Na Lixeira</h3>{etapasNaLixeira.length ? etapasNaLixeira.map((etapa) => <div key={etapa.id} className="agset-stage-recovery-row"><strong>{etapa.nome}</strong><span>{contagem(etapa.id)} paciente(s) preservado(s)</span>{isAdmin && <button type="button" className="agset-pipeline-button ghost" onClick={() => alterarEstadoEtapa(etapa, 'ativa')}><RotateCcw size={14} /> Restaurar</button>}</div>) : <p>Nenhuma carteira na Lixeira.</p>}<div className="agset-pipeline-modal-foot"><button type="button" className="agset-pipeline-button ghost" onClick={() => setLixeira(false)}>Fechar</button></div></section></div>}

    {historicoModal && <div className="agset-pipeline-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && setHistoricoModal(null)}><div className="agset-history-modal"><div className="agset-pipeline-modal-head"><div><h2>Histórico do registro</h2><p>{historicoModal.item.nome}</p></div><button type="button" onClick={() => setHistoricoModal(null)}><X size={18} /></button></div><div className="agset-history-list">{historicoModal.eventos.length ? historicoModal.eventos.map((evento, index) => <div className="agset-history-item" key={index}><History size={15} /><div><strong>{evento.descricao || evento.acao || `Campo “${evento.campo || 'registro'}” atualizado`}</strong><span>{evento.valor || evento.novo_valor || evento.status || ''}</span><small>{evento.data ? new Date(evento.data).toLocaleString('pt-BR') : 'Agora'}</small></div></div>) : <p>Nenhuma alteração registrada.</p>}</div><div className="agset-history-foot">Histórico da base de demonstração.<button className="agset-pipeline-button ghost" onClick={() => setHistoricoModal(null)}>Fechar</button></div></div></div>}
    {importacao && (() => {
      const aImportar = listaParaImportar(importacao);
      const duplicadas = importacao.validas.length - aImportar.length;
      const pct = importacao.total ? Math.round((importacao.feitos / importacao.total) * 100) : 0;
      const fechar = () => { if (!importacao.executando) setImportacao(null); };
      return <div className="agset-pipeline-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && fechar()}>
        <div className="agset-pipeline-modal agset-import-modal">
          <div className="agset-pipeline-modal-head">
            <div><h2>Importar pacientes</h2><p>{importacao.arquivo} · aba {importacao.aba}</p></div>
            <button type="button" onClick={fechar} aria-label="Fechar"><X size={18} /></button>
          </div>

          {!importacao.concluido ? <>
            <div className="agset-import-resumo">
              <div><strong>{aImportar.length}</strong><span>serão importados</span></div>
              <div><strong>{duplicadas}</strong><span>já existem na etapa</span></div>
              <div><strong>{importacao.semNome}</strong><span>sem nome (ignorados)</span></div>
              <div><strong>{importacao.datasInvalidas}</strong><span>data inválida (entram sem data)</span></div>
            </div>
            <p className="agset-import-colunas"><strong>Colunas reconhecidas:</strong> {importacao.colunas.join(' · ') || 'nenhuma — usando 1ª coluna como nome e 2ª como telefone'}</p>

            <label className="agset-import-campo">Etapa de destino
              <select value={importacao.etapa} disabled={importacao.executando} onChange={(e) => setImportacao((atual) => ({ ...atual, etapa: e.target.value }))}>
                {etapasAtivas.map((etapa) => <option key={etapa.id} value={etapa.id}>{etapa.nome}</option>)}
              </select>
            </label>
            <label className="agset-import-check">
              <input type="checkbox" checked={importacao.ignorarDuplicados} disabled={importacao.executando} onChange={(e) => setImportacao((atual) => ({ ...atual, ignorarDuplicados: e.target.checked }))} />
              Não importar pacientes que já existem nesta etapa (mesmo nome e telefone)
            </label>
            <label className="agset-import-check">
              <input type="checkbox" checked={importacao.atualizarExistentes} disabled={importacao.executando} onChange={(e) => setImportacao((atual) => ({ ...atual, atualizarExistentes: e.target.checked }))} />
              Atualizar Tratamentos, Valor, Dentista e Virou Ficha de quem já existe nesta etapa
            </label>

            <div className="agset-import-previa">
              <table>
                <thead><tr><th>Nome</th><th>Telefone</th><th>Tratamentos</th><th>Valor</th><th>Dentista</th><th>Ficha</th><th>Status</th><th>Data</th></tr></thead>
                <tbody>
                  {importacao.validas.slice(0, 8).map((l, i) => <tr key={i}><td>{l.nome}</td><td>{l.contato || '—'}</td><td>{l.tratamentos || '—'}</td><td>{l.valor_total || '—'}</td><td>{l.dentista || '—'}</td><td>{({ sim: 'Sim', nao: 'Não' })[l.virou_ficha] || '—'}</td><td>{statusNome(l.status)}</td><td>{l.data ? dataBR(l.data) : '—'}</td></tr>)}
                </tbody>
              </table>
            </div>

            {importacao.executando && <div className="agset-import-progresso">
              <span>Importando {importacao.feitos} de {importacao.total}…</span>
              <div className="agset-import-barra"><span style={{ width: `${pct}%` }} /></div>
            </div>}

            <div className="agset-pipeline-modal-foot"><span />
              <div>
                <button type="button" className="agset-pipeline-button ghost" onClick={fechar} disabled={importacao.executando}>Cancelar</button>
                <button type="button" className="agset-pipeline-button primary" onClick={executarImportacao} disabled={importacao.executando || (!aImportar.length && !(importacao.atualizarExistentes && duplicadas))}>
                  <Upload size={14} /> {importacao.executando ? 'Importando…' : `Importar ${aImportar.length}${importacao.atualizarExistentes && duplicadas ? ` e atualizar ${duplicadas}` : ''} paciente(s)`}
                </button>
              </div>
            </div>
          </> : <>
            <div className="agset-import-final">
              <strong>{importacao.importados} paciente(s) importado(s)</strong>{importacao.atualizados > 0 && <strong>{importacao.atualizados} paciente(s) atualizado(s)</strong>}
              {importacao.falhas > 0 && <span>{importacao.falhas} linha(s) não puderam ser gravadas.</span>}
            </div>
            <div className="agset-pipeline-modal-foot"><span /><div><button type="button" className="agset-pipeline-button primary" onClick={() => setImportacao(null)}>Fechar</button></div></div>
          </>}
        </div>
      </div>;
    })()}
    {modalNovo && <div className="agset-pipeline-modal-backdrop\" onMouseDown={(e) => e.target === e.currentTarget && setModalNovo(false)}><form className="agset-pipeline-modal" onSubmit={criarPaciente}><div className="agset-pipeline-modal-head"><div><h2>Novo paciente</h2><p>O registro será salvo na planilha de demonstração.</p></div><button type="button" onClick={() => setModalNovo(false)}><X size={18} /></button></div><label>Nome <em>*</em><input required autoFocus placeholder="Nome do paciente" value={novo.nome} onChange={(e) => setNovo({ ...novo, nome: e.target.value })} /></label><div className="agset-modal-two-cols"><label>Contato (telefone)<input placeholder="(00) 00000-0000" value={novo.contato} onChange={(e) => setNovo({ ...novo, contato: e.target.value })} /></label><label>Status inicial<select value={novo.status} onChange={(e) => setNovo({ ...novo, status: e.target.value })}>{STATUS.map(([valor, nome]) => <option key={valor} value={valor}>{nome}</option>)}</select></label></div><div className="agset-modal-two-cols"><label>Data do envio<input type="date" value={novo.data} onChange={(e) => setNovo({ ...novo, data: e.target.value })} /></label><label>Horário<input type="time" value={novo.horario} onChange={(e) => setNovo({ ...novo, horario: e.target.value })} placeholder="09:00" /></label></div><div className="agset-modal-two-cols"><label>Planilha inicial<select value={novo.etapa} onChange={(e) => setNovo({ ...novo, etapa: e.target.value })}>{etapasAtivas.map((etapa) => <option key={etapa.id} value={etapa.id}>{etapa.nome}</option>)}</select></label></div><label>Observações<textarea placeholder="Anotações para a equipe..." value={novo.observacao} onChange={(e) => setNovo({ ...novo, observacao: e.target.value })} /></label><div className="agset-pipeline-modal-foot"><span><input type="checkbox" defaultChecked /> Use somente dados fictícios nesta demonstração.</span><div><button type="button" className="agset-pipeline-button ghost" onClick={() => setModalNovo(false)}>Cancelar</button><button className="agset-pipeline-button primary"><Plus size={14} /> Salvar paciente</button></div></div></form></div>}
  </main>;
}
